import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { Breakpoint, SxProps, Theme } from '@mui/material';
import MuiDialog, { DialogSlots } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { Box } from './Box';
import { Button, ButtonProps } from './Button';
import { Divider } from './Divider';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { SafeArea } from './SafeArea';
import { Stack } from './Stack';
import { TitleAndClose } from './TitleAndClose';
import { Typography } from './Typography';

export interface ButtonPropsWithLabel extends ButtonProps {
  label?: string | React.ReactNode;
  target?: string;
}

export interface DialogProps {
  open: boolean;
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  titleAlignment?: 'left' | 'center';
  sx?: SxProps<Theme>;
  onClose?: () => void;
  fullScreen?: boolean;
  fullWidth?: boolean;
  titleVariant?: 'h5' | 'h6';
  leftAdornment?: React.ReactNode;
  rightAdornment?: React.ReactNode;
  actions?: React.ReactNode | ButtonPropsWithLabel[];
  actionsJustify?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  dividers?: boolean;
  noWrap?: boolean;
  spacing?: number;
  children: React.ReactNode;
  maxWidth?: false | Breakpoint;
  slots?: Partial<DialogSlots>;
  keepMounted?: boolean;
  variant?: 'regular' | 'compact';
}

export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  titleAlignment = 'left',
  sx,
  titleVariant,
  fullScreen,
  leftAdornment,
  rightAdornment,
  actions,
  actionsJustify,
  dividers: _dividers = false,
  fullWidth = true,
  noWrap = false,
  spacing: _spacing,
  children,
  maxWidth,
  slots,
  keepMounted,
  variant,
}) => {
  const mainWhitespace = _spacing ?? 2;
  const showTitleSpace = title || onClose || rightAdornment || leftAdornment;
  const dividers = variant === 'compact' ? true : _dividers;

  const styles = {
    ...(!fullScreen && {
      '& .MuiDialog-paper': { mx: 2, width: 'calc(100% - 32px)' },
    }),
    ...sx,
  };
  return (
    <MuiDialog
      disableRestoreFocus
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      sx={styles}
      scroll="paper"
      slots={slots}
      keepMounted={keepMounted}
    >
      {showTitleSpace ? (
        <SafeArea top={fullScreen} mode="padding">
          {variant === 'compact' ? (
            <Stack
              direction="row"
              sx={{
                pl: 3,
                pr: 2,
                py: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                sx={{ height: '100%' }}
              >
                {leftAdornment}
                <Box>
                  <Typography variant={titleVariant ?? 'h5'}>
                    {title}
                  </Typography>
                  {subtitle && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        ml: '1px',
                      }}
                    >
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Box>
                {rightAdornment}
                {onClose && (
                  <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                      color: (theme) => theme.vars.palette.neutral[10],
                      ml: rightAdornment ? 1 : 0,
                    }}
                    size="small"
                  >
                    <Icon color="action" icon={faTimes} size="small" />
                  </IconButton>
                )}
              </Box>
            </Stack>
          ) : (
            <TitleAndClose
              title={title}
              subtitle={subtitle}
              titleAlignment={titleAlignment}
              onClose={onClose}
              noWrap={noWrap}
              leftAdornment={leftAdornment}
              rightAdornment={rightAdornment}
              titleVariant={titleVariant}
            ></TitleAndClose>
          )}
        </SafeArea>
      ) : (
        <Box mt={mainWhitespace}></Box>
      )}
      {dividers && <Divider />}
      <DialogContent
        sx={{
          p: mainWhitespace,
          pt: dividers ? mainWhitespace : 0,
          ...(variant === 'compact' && { px: 3, py: 1.5 }),
          position: 'relative',
          bgcolor:
            variant === 'compact' ? 'background.level1' : 'background.default',
        }}
      >
        {children}
      </DialogContent>

      {dividers && !!actions && <Divider />}
      {!!actions && (
        <SafeArea bottom={fullScreen} mode="padding">
          <DialogActions
            sx={{
              p: mainWhitespace,
              justifyContent: actionsJustify,
              ...(variant === 'compact' && { px: 3, py: 1.5 }),
            }}
          >
            {Array.isArray(actions) &&
              actions.map((buttonProps, i) => (
                <Button key={i} {...buttonProps}>
                  {buttonProps.children || buttonProps.label}
                </Button>
              ))}
            {!Array.isArray(actions) && actions}
          </DialogActions>
        </SafeArea>
      )}
    </MuiDialog>
  );
};
